import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { admiBuildingsService, RealEstateWithAssets } from 'src/features/real-estate/admi-buildings.service';
import { Building, RealEstateAssetSummary } from 'src/features/real-estate/buildings.dto';
import { LocationSearch } from 'src/features/real-estate/buildings.dto';
import { admiRealEstateAssetService } from './admi-real-estate-asset.service';
import {
    AddBuildingToAssetRequest,
    CreateRealEstateAssetRequest,
    CreateServiceAssetMappingRequest,
    DealWithServices,
    RealEstateAsset,
    ServiceAssetMapping,
    UpdateRealEstateAssetRequest,
} from './real-estate-asset-management.dto';

interface RealEstateAssetManagementState {
    buildings: Building[];
    realEstateAssets: RealEstateAssetSummary[];
    isLoadingBuildings: boolean;
    selectedRea: RealEstateAsset | null;
    isLoadingRea: boolean;
    isMutating: boolean;
    errorMessage: string | null;
    lastLocationSearch: LocationSearch | null;
    reaServices: DealWithServices[];
    /** REA id the current `reaServices` belongs to, or `null` until first fetch completes. */
    reaServicesLoadedForId: string | null;
    isLoadingReaServices: boolean;
    unassignedServices: DealWithServices[];
    isLoadingUnassignedServices: boolean;
    isMutatingServiceMapping: boolean;
}

const initialState: RealEstateAssetManagementState = {
    buildings: [],
    realEstateAssets: [],
    isLoadingBuildings: false,
    selectedRea: null,
    isLoadingRea: false,
    isMutating: false,
    errorMessage: null,
    lastLocationSearch: null,
    reaServices: [],
    reaServicesLoadedForId: null,
    isLoadingReaServices: false,
    unassignedServices: [],
    isLoadingUnassignedServices: false,
    isMutatingServiceMapping: false,
};

export const loadBuildingsWithAssets = createAsyncThunk<RealEstateWithAssets, LocationSearch>(
    'realEstateAssetManagement/loadBuildingsWithAssets',
    async (locationSearch) => admiBuildingsService.loadBuildingsDataWithAssetsV2(locationSearch),
);

export const fetchRealEstateAsset = createAsyncThunk<RealEstateAsset, string>(
    'realEstateAssetManagement/fetchRealEstateAsset',
    async (id) => admiRealEstateAssetService.getRealEstateAsset(id),
);

export const createRealEstateAsset = createAsyncThunk<RealEstateAsset, CreateRealEstateAssetRequest>(
    'realEstateAssetManagement/createRealEstateAsset',
    async (request, { rejectWithValue }) => {
        try {
            return await admiRealEstateAssetService.createRealEstateAsset(request);
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const updateRealEstateAsset = createAsyncThunk<
    RealEstateAsset,
    { assetId: string; request: UpdateRealEstateAssetRequest }
>('realEstateAssetManagement/updateRealEstateAsset', async ({ assetId, request }, { rejectWithValue }) => {
    try {
        return await admiRealEstateAssetService.updateRealEstateAsset(assetId, request);
    } catch (e) {
        return rejectWithValue(e);
    }
});

export const addBuildingToAsset = createAsyncThunk<void, { assetId: string; request: AddBuildingToAssetRequest }>(
    'realEstateAssetManagement/addBuildingToAsset',
    async ({ assetId, request }, { rejectWithValue }) => {
        try {
            await admiRealEstateAssetService.addBuildingToAsset(assetId, request);
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const removeBuildingFromAsset = createAsyncThunk<void, { assetId: string; buildingId: string }>(
    'realEstateAssetManagement/removeBuildingFromAsset',
    async ({ assetId, buildingId }, { rejectWithValue }) => {
        try {
            await admiRealEstateAssetService.removeBuildingFromAsset(assetId, buildingId);
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const fetchRealEstateAssetServices = createAsyncThunk<DealWithServices[], string>(
    'realEstateAssetManagement/fetchRealEstateAssetServices',
    async (assetId) => {
        const response = await admiRealEstateAssetService.getRealEstateAssetServices(assetId);
        return response.deals;
    },
);

export const fetchUnassignedServices = createAsyncThunk<DealWithServices[]>(
    'realEstateAssetManagement/fetchUnassignedServices',
    async () => {
        const response = await admiRealEstateAssetService.getUnassignedServices();
        return response.deals;
    },
);

export const createServiceAssetMapping = createAsyncThunk<
    ServiceAssetMapping,
    { serviceId: string; request: CreateServiceAssetMappingRequest }
>('realEstateAssetManagement/createServiceAssetMapping', async ({ serviceId, request }, { rejectWithValue }) => {
    try {
        return await admiRealEstateAssetService.createServiceAssetMapping(serviceId, request);
    } catch (e) {
        return rejectWithValue(e);
    }
});

export const deleteServiceAssetMapping = createAsyncThunk<void, { serviceId: string }>(
    'realEstateAssetManagement/deleteServiceAssetMapping',
    async ({ serviceId }, { rejectWithValue }) => {
        try {
            await admiRealEstateAssetService.deleteServiceAssetMapping(serviceId);
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

const realEstateAssetManagementSlice = createSlice({
    name: 'realEstateAssetManagement',
    initialState,
    reducers: {
        clearSelectedRea(state) {
            state.selectedRea = null;
            // Reset the services cache too, so the groups effect's readiness gate
            // (reaServicesLoadedForId === selectedRea.id) closes until fresh services
            // land — otherwise re-selecting the same REA fetches groups from stale data.
            state.reaServices = [];
            state.reaServicesLoadedForId = null;
        },
        clearError(state) {
            state.errorMessage = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadBuildingsWithAssets.pending, (state, action) => {
                state.isLoadingBuildings = true;
                state.lastLocationSearch = action.meta.arg;
            })
            .addCase(loadBuildingsWithAssets.fulfilled, (state, action) => {
                state.isLoadingBuildings = false;
                state.buildings = action.payload.buildings;
                state.realEstateAssets = action.payload.realEstateAssets;
            })
            .addCase(loadBuildingsWithAssets.rejected, (state) => {
                state.isLoadingBuildings = false;
                state.errorMessage = 'Failed to load buildings';
            })

            .addCase(fetchRealEstateAsset.pending, (state) => {
                state.isLoadingRea = true;
            })
            .addCase(fetchRealEstateAsset.fulfilled, (state, action) => {
                state.isLoadingRea = false;
                state.selectedRea = action.payload;
            })
            .addCase(fetchRealEstateAsset.rejected, (state) => {
                state.isLoadingRea = false;
                state.errorMessage = 'Real estate asset not found.';
            })

            .addCase(createRealEstateAsset.pending, (state) => {
                state.isMutating = true;
            })
            .addCase(createRealEstateAsset.fulfilled, (state, action) => {
                state.isMutating = false;
                state.selectedRea = action.payload;
                const { id, name, code, buildings } = action.payload;
                state.realEstateAssets = [
                    ...state.realEstateAssets.filter((r) => r.id !== id),
                    { id, name, code, buildings },
                ];
            })
            .addCase(createRealEstateAsset.rejected, (state) => {
                state.isMutating = false;
            })

            .addCase(updateRealEstateAsset.pending, (state) => {
                state.isMutating = true;
            })
            .addCase(updateRealEstateAsset.fulfilled, (state, action) => {
                state.isMutating = false;
                state.selectedRea = action.payload;
            })
            .addCase(updateRealEstateAsset.rejected, (state) => {
                state.isMutating = false;
            })

            .addCase(addBuildingToAsset.pending, (state) => {
                state.isMutating = true;
            })
            .addCase(addBuildingToAsset.fulfilled, (state) => {
                state.isMutating = false;
            })
            .addCase(addBuildingToAsset.rejected, (state) => {
                state.isMutating = false;
            })

            .addCase(removeBuildingFromAsset.pending, (state) => {
                state.isMutating = true;
            })
            .addCase(removeBuildingFromAsset.fulfilled, (state) => {
                state.isMutating = false;
            })
            .addCase(removeBuildingFromAsset.rejected, (state) => {
                state.isMutating = false;
            })

            .addCase(fetchRealEstateAssetServices.pending, (state, action) => {
                state.isLoadingReaServices = true;
                // Clear stale data when switching REAs so the Services-tab
                // warning icon doesn't compute from the previous REA's services.
                if (state.reaServicesLoadedForId !== action.meta.arg) {
                    state.reaServices = [];
                    state.reaServicesLoadedForId = null;
                }
            })
            .addCase(fetchRealEstateAssetServices.fulfilled, (state, action) => {
                state.isLoadingReaServices = false;
                state.reaServices = action.payload;
                state.reaServicesLoadedForId = action.meta.arg;
            })
            .addCase(fetchRealEstateAssetServices.rejected, (state) => {
                state.isLoadingReaServices = false;
                state.errorMessage = 'Failed to load services for real estate asset.';
            })

            .addCase(fetchUnassignedServices.pending, (state) => {
                state.isLoadingUnassignedServices = true;
            })
            .addCase(fetchUnassignedServices.fulfilled, (state, action) => {
                state.isLoadingUnassignedServices = false;
                state.unassignedServices = action.payload;
            })
            .addCase(fetchUnassignedServices.rejected, (state) => {
                state.isLoadingUnassignedServices = false;
                state.errorMessage = 'Failed to load unassigned services.';
            })

            .addCase(createServiceAssetMapping.pending, (state) => {
                state.isMutatingServiceMapping = true;
            })
            .addCase(createServiceAssetMapping.fulfilled, (state) => {
                state.isMutatingServiceMapping = false;
            })
            .addCase(createServiceAssetMapping.rejected, (state) => {
                state.isMutatingServiceMapping = false;
            })

            .addCase(deleteServiceAssetMapping.pending, (state) => {
                state.isMutatingServiceMapping = true;
            })
            .addCase(deleteServiceAssetMapping.fulfilled, (state) => {
                state.isMutatingServiceMapping = false;
            })
            .addCase(deleteServiceAssetMapping.rejected, (state) => {
                state.isMutatingServiceMapping = false;
            });
    },
});

export const { clearSelectedRea, clearError } = realEstateAssetManagementSlice.actions;
export default realEstateAssetManagementSlice.reducer;
